import formatMoney from './formatMoney';
import { getDisplayCents, getVatCents, getMarketRatePercent, readVatConfig } from './vatBasis';
import { refreshPdpSaleBadgesFromDom } from './variants/formatPdpSaleSummary';

const STORAGE_KEY = 'rs_vat_display_mode';
const MODE_EX = 'ex';
const MODE_INC = 'inc';
const ATTR_VAT_DISPLAY = 'data-vat-display';

const vatCallbacks = [];

const readConfig = () => readVatConfig();

export { getDisplayCents, getDisplaySavingsCents, getDisplaySavingsPercent } from './vatBasis';

export const formatDisplayMoney = (canonicalCents) => {
  return formatMoney(getDisplayCents(canonicalCents), window.theme.moneyFormat);
};

export const refreshAllVatPrices = () => {
  if (!readConfig()) return;
  const fmt = window.theme.moneyFormat;
  document.querySelectorAll('[data-vat-amount]').forEach((el) => {
    const raw = el.getAttribute('data-vat-amount');
    const cents = parseInt(raw, 10);
    if (Number.isNaN(cents)) return;

    const srOnly = [...el.children].find((c) => c.classList?.contains('sr-only'));
    if (srOnly) {
      const formatted = formatMoney(getDisplayCents(cents), fmt);
      let n = srOnly.nextSibling;
      while (n) {
        const next = n.nextSibling;
        el.removeChild(n);
        n = next;
      }
      el.appendChild(document.createTextNode(formatted));
      return;
    }

    el.textContent = formatMoney(getDisplayCents(cents), fmt);
  });
};

/** Recompute VAT breakdown rows + rate labels for the visitor's market. */
export const refreshVatLineItems = () => {
  if (!readConfig()) return;
  const fmt = window.theme.moneyFormat;
  const rate = getMarketRatePercent();
  const untaxed = rate <= 0;

  document.querySelectorAll('[data-vat-line-row]').forEach((row) => {
    row.hidden = untaxed;
  });
  document.querySelectorAll('[data-vat-line-amount]').forEach((el) => {
    const cents = parseInt(el.getAttribute('data-vat-line-amount'), 10);
    if (Number.isNaN(cents)) return;
    el.textContent = formatMoney(getVatCents(cents), fmt);
  });
  document.querySelectorAll('[data-vat-rate-label]').forEach((el) => {
    el.textContent = String(rate);
  });
};

const syncVatLabels = () => {
  if (!readConfig()) return;
  const inc = window.theme?.vat?.labelInc;
  const ex = window.theme?.vat?.labelEx;
  if (typeof inc !== 'string' || typeof ex !== 'string') return;
  const mode =
    document.documentElement.getAttribute(ATTR_VAT_DISPLAY) ||
    (window.theme?.vat?.defaultMode === MODE_EX ? MODE_EX : MODE_INC);
  const text = mode === MODE_EX ? ex : inc;
  document.querySelectorAll('[data-vat-label]').forEach((el) => {
    el.textContent = text;
  });
};

/** PDP / dynamic price rows re-apply variant math after VAT toggle */
export const registerVatRefresh = (fn) => {
  if (typeof fn === 'function') {
    vatCallbacks.push(fn);
  }
};

export const notifyVatRefresh = () => {
  refreshAllVatPrices();
  refreshVatLineItems();
  syncVatLabels();
  vatCallbacks.forEach((cb) => {
    try {
      cb();
    } catch (e) {
      console.error(e);
    }
  });
  refreshPdpSaleBadgesFromDom();
};

const syncToggleUi = (root, mode) => {
  if (!root) return;
  root.querySelectorAll('[data-vat-pill]').forEach((pill) => {
    const m = pill.getAttribute('data-vat-pill');
    const active = m === mode;
    pill.classList.toggle('border-white/90', active);
    pill.classList.toggle('bg-white', active);
    pill.classList.toggle('opacity-100', active);
    pill.classList.toggle('border-transparent', !active);
    pill.classList.toggle('bg-transparent', !active);
    pill.classList.toggle('opacity-60', !active);
  });
};

const syncAllToggleUi = (mode) => {
  document.querySelectorAll('[data-vat-controls]').forEach((root) => {
    syncToggleUi(root, mode);
  });
};

/**
 * Bind VAT toggle + set initial mode. Call `notifyVatRefresh` in a setTimeout(0)
 * so product template can register callbacks first.
 */
export function initVatDisplay() {
  if (!readConfig()) return;

  const def = window.theme.vat.defaultMode === MODE_EX ? MODE_EX : MODE_INC;
  const stored = localStorage.getItem(STORAGE_KEY);
  const mode = stored === MODE_EX || stored === MODE_INC ? stored : def;
  document.documentElement.setAttribute(ATTR_VAT_DISPLAY, mode);

  const roots = document.querySelectorAll('[data-vat-controls]');
  if (roots.length) {
    syncAllToggleUi(mode);
    roots.forEach((root) => {
      root.addEventListener('click', () => {
        const current =
          document.documentElement.getAttribute(ATTR_VAT_DISPLAY) ||
          (window.theme?.vat?.defaultMode === MODE_EX ? MODE_EX : MODE_INC);
        const next = current === MODE_EX ? MODE_INC : MODE_EX;
        document.documentElement.setAttribute(ATTR_VAT_DISPLAY, next);
        localStorage.setItem(STORAGE_KEY, next);
        syncAllToggleUi(next);
        notifyVatRefresh();
      });
    });
  }

  setTimeout(() => notifyVatRefresh(), 0);
}
